/**
 * La hoja de las plantillas oficiales y cómo se traducen sus coordenadas.
 *
 * ⚠ **Todo se declara en PUNTOS, tal como venían en el PDF original, y se convierte
 * a milímetros en un solo sitio.** Las cifras de estos formatos no se han medido a
 * ojo ni se han redondeado: salen de `get_drawings()` y `get_text()` sobre el PDF que
 * entrega la aduana, así que cada caja está donde está en el documento oficial. Si se
 * pasaran a milímetros «para que se lean mejor» habría que redondear ochenta números,
 * y cada redondeo es un milímetro de deriva contra el papel que espera el
 * funcionario.
 *
 * ⚠ **La hoja es A4 y NO Carta.** El original mide 595,5 × 842,25 pt —210 × 297 mm—,
 * que es lo que el resto del sistema no usa: rótulos y carnets se imprimen en Carta.
 * Por eso `globales.css` declara una `@page` con nombre para estas hojas: con la
 * Carta global, los 297 mm de alto no caben en 279 y la hoja saldría partida en dos.
 */

/** La hoja, en milímetros. A4 exacta. */
export const HOJA_A4 = { ancho: 210, alto: 297 } as const

/**
 * De puntos PostScript a milímetros. 72 pt = 1 pulgada = 25,4 mm.
 *
 * ⚠ **Devuelve el número, no la cadena con unidad.** Quien lo usa compone
 * `${pt(x)}mm`, y así la misma función sirve para sumar dos medidas antes de
 * pintarlas — que es lo que hace falta para centrar un texto dentro de su caja.
 */
export function pt(puntos: number): number {
  return (puntos * 25.4) / 72
}

/**
 * Los nombres de la tinta del formato. **Los valores viven en `globales.css`**, en
 * `.hoja-plantilla`, igual que los de `.pieza-impresa`: ningún hex literal en un
 * componente (ver `.claude/rules/frontend.md`).
 *
 * ⚠ **Son los colores del documento OFICIAL, no los de SETIECA.** Esto no es
 * papelería de la agencia: es un formato del SENIAT que la agencia rellena y entrega,
 * y el funcionario que lo recibe espera verlo como lo conoce. Por eso no lleva ni el
 * vino de la marca ni el lenguaje de `componentes/impresos/` — es la única parte del
 * sistema donde la fidelidad a un tercero manda sobre la coherencia con el resto.
 */
export const TINTA = {
  /** El azul marino del título, del pie y de «Aduana Principal de Puerto Cabello». */
  oscura: "var(--plantilla-oscura)",
  /** El azul medio de los rótulos de cada campo. */
  rotulo: "var(--plantilla-rotulo)",
  /** El relleno de las cajas rellenables. */
  caja: "var(--plantilla-caja)",
  /** El panel redondeado de la cabecera. */
  panel: "var(--plantilla-panel)",
  papel: "var(--plantilla-papel)",
} as const

/**
 * La cabecera, idéntica en los dos formatos.
 *
 * Coordenadas en pt sobre la hoja, con el origen arriba a la izquierda.
 *
 * ⚠ **Los rótulos de 10 pt van 2,74 pt más abajo que en el PDF, y no es un
 * descuadre.** El original escribe los rótulos de campo en Open Sans y todo lo demás
 * en Inter; aquí se pinta todo en Inter, que tiene el ascendente más bajo —0,969 em
 * frente a 0,927— así que a la misma `y` la tinta cae alta. Se midió comparando la
 * primera fila con tinta de cinco rótulos contra el PDF renderizado: 2,74 pt en las
 * cinco, con varianza cero. Los de 7, 8, 8,7, 11,2 y 14,1 pt ya caen dentro de un
 * píxel y no llevan corrección.
 */
export const CABECERA = {
  panel: { x: 45.5, y: 28.9, w: 512.1, h: 56.9 },
  logo: { x: 74.1, y: 31.5, w: 94.6, h: 43.5 },
  aduana: { x: 60.7, y: 71.5 },
  titulo: { x: 208, y: 28.4, w: 210 },
  /** Las ocho casillas de régimen, en dos filas de cuatro. */
  casillas: [
    { clave: "ENA", etiqueta: { x: 387.5, y: 54.94 }, caja: { x: 413.6, y: 53.9 } },
    { clave: "RE", etiqueta: { x: 433.9, y: 54.94 }, caja: { x: 450.8, y: 54.0 } },
    { clave: "IMA", etiqueta: { x: 470.4, y: 54.94 }, caja: { x: 495.3, y: 54.2 } },
    { clave: "AT", etiqueta: { x: 516.5, y: 54.94 }, caja: { x: 534.9, y: 54.3 } },
    { clave: "IM", etiqueta: { x: 393.0, y: 69.44 }, caja: { x: 413.6, y: 68.8 } },
    { clave: "ID", etiqueta: { x: 433.9, y: 69.44 }, caja: { x: 451.0, y: 69.6 } },
    { clave: "RI", etiqueta: { x: 478.7, y: 69.44 }, caja: { x: 496.3, y: 69.6 } },
    { clave: "TR", etiqueta: { x: 516.3, y: 69.44 }, caja: { x: 535.3, y: 68.8 } },
  ],
  /** Cada casilla mide lo mismo. */
  casilla: { w: 14.9, h: 13 },
  fechaRotulo: { x: 284.8, y: 96.84 },
  fecha: {
    dia: { micro: { x: 408.4, y: 85.5 }, caja: { x: 388.4, y: 95.8, w: 51.4, h: 17.9 } },
    mes: { micro: { x: 463.6, y: 86.0 }, caja: { x: 444.0, y: 95.8, w: 49.2, h: 18.4 } },
    anio: { micro: { x: 513.8, y: 85.5 }, caja: { x: 497.7, y: 95.8, w: 60.1, h: 18.4 } },
  },
} as const

/**
 * El bloque del responsable, por formato.
 *
 * ⚠ **Los tres renglones empiezan y acaban en la misma vertical**, aunque en el PDF
 * sean tres cadenas de texto con distinto número de espacios delante. Ahí las rayas
 * son guiones bajos dentro del propio texto —«Teléfono:______»— y quedan alineadas
 * por casualidad aritmética: 24 espacios y 9 letras ocupan casi lo mismo que 8
 * espacios y 18 letras. Reproducir esa casualidad con espacios sería heredarla; aquí
 * la etiqueta se alinea a la derecha contra `x` y la raya arranca justo ahí.
 *
 * ⚠ **El bloque entero va 8,4 pt más a la izquierda en «Vehículos».** No es un
 * descuadre nuestro: los dos formatos lo traen así, y corregirlo «porque queda mejor»
 * sería dejar de reproducirlos.
 */
export const PIE = {
  POSICIONAMIENTO: {
    y: 289,
    /** Dónde acaba la etiqueta y empieza la raya. */
    x: 148.2,
    responsable: 140,
    nota: {
      x: 367,
      y: 326.1,
      lineas: ["NOTA:  DE SER MAS DE 3 CONTENEDORES", "SE DEBE ENTREGAR COPIA DEL BL"],
    },
  },
  VEHICULOS: {
    y: 270.9,
    x: 139.8,
    responsable: 131.6,
    nota: {
      x: 365.9,
      y: 309.1,
      lineas: ["NOTA:  LLENAR 1 FORMATO POR MODELO", "DE VEHICULO"],
    },
  },
  /** Cuánto baja de una línea a la siguiente, y dónde cae la primera. */
  salto: 25.8,
  primera: -9.4,
  /**
   * El ancho de la raya.
   *
   * ⚠ **Medido, no estimado.** El primer intento fue contar guiones bajos y
   * multiplicar por el ancho de uno: daba 190 pt y las rayas salían 14 pt largas y
   * empezaban 14 pt antes. Lo que vale es dónde acaba la tinta del original —324,0 pt
   * en la solicitud, 315,7 en vehículos— menos dónde empieza la raya.
   */
  renglon: 175.8,
  lineas: [
    { etiqueta: "Nombre y Apellido:", campo: "nombre" },
    { etiqueta: "Teléfono:", campo: "telefono" },
    { etiqueta: "Firma:", campo: null },
  ],
} as const
